import type {
  CustomAnalyticsReportStatus,
  CustomAnalyticsReportSummary,
} from "@redotech/merchant-sdk/rpc/schema/analytics/reports";
import type { PageContext } from "@redotech/redo-agents-common/page-context";
import {
  type CustomAnalyticsReportColumn,
  type CustomAnalyticsReportSort,
} from "@redotech/redo-analytics-common/reports/custom-analytics-report";
import { getFriendlyTimezoneOption } from "@redotech/redo-common-model/timezone-utils";
import { SortDirection } from "@redotech/redo-views-common/table";
import {
  CUSTOM_REPORT_HEADER_AVERAGE_CHARACTER_WIDTH_PX,
  CUSTOM_REPORT_HEADER_HELP_ICON_WIDTH_PX,
  CUSTOM_REPORT_HEADER_HORIZONTAL_PADDING_PX,
  CUSTOM_REPORT_HEADER_SORT_ICON_WIDTH_PX,
} from "./custom-analytics-report-page-constants";
import type {
  BuildCustomReportPageContextParams,
  BuildCustomReportRowsPageDataSourceParams,
  CustomAnalyticsReportPageDataSource,
  CustomAnalyticsReportPageDataSourceStatus,
} from "./custom-analytics-report-page-types";

const CUSTOM_REPORT_PAGE_CONTEXT_SAMPLE_ROW_LIMIT = 100;
const CUSTOM_REPORT_PAGE_CONTEXT_ARRAY_ITEM_LIMIT = 5;
const CUSTOM_REPORT_PAGE_CONTEXT_OBJECT_KEY_LIMIT = 10;
const CUSTOM_REPORT_PAGE_CONTEXT_STRING_LENGTH_LIMIT = 160;
const CUSTOM_REPORT_PAGE_CONTEXT_SAMPLE_TOTAL_CHAR_LIMIT = 50_000;
const CUSTOM_REPORT_PAGE_SUGGESTED_QUESTION_LIMIT = 2;
const CUSTOM_REPORT_PAGE_ADD_USEFUL_COLUMNS_QUESTION =
  "Add more useful columns to this report";
const CUSTOM_REPORT_PAGE_EXPLAIN_REPORT_QUESTION = "Explain this report";
const CUSTOM_REPORT_METRIC_COLUMN_KINDS: ReadonlySet<
  CustomAnalyticsReportColumn["kind"]
> = new Set(["number", "integer", "currency", "percent"]);
const DELETABLE_CUSTOM_ANALYTICS_REPORT_STATUSES: readonly CustomAnalyticsReportStatus[] =
  ["ready", "failed"];

export function isCustomAnalyticsReportDeletable(
  status: CustomAnalyticsReportStatus | null | undefined,
) {
  return (
    !!status && DELETABLE_CUSTOM_ANALYTICS_REPORT_STATUSES.includes(status)
  );
}

export function getColumnMinLength(
  column: Pick<CustomAnalyticsReportColumn, "kind" | "label">,
): number {
  return Math.max(
    getColumnValueMinLength(column),
    getColumnHeaderMinLength(column),
  );
}

export function getNextCustomAnalyticsReportSort({
  column,
  currentSort,
}: {
  column: CustomAnalyticsReportColumn;
  currentSort: CustomAnalyticsReportSort | null;
}): CustomAnalyticsReportSort {
  return {
    key: column.key,
    direction:
      currentSort?.key === column.key &&
      currentSort.direction === SortDirection.DESC
        ? SortDirection.ASC
        : SortDirection.DESC,
  };
}

export function getReportTitle({
  cachedTitle,
  report,
}: {
  cachedTitle?: string;
  report?: Pick<
    CustomAnalyticsReportSummary,
    "title" | "status" | "storedTitle"
  >;
}): string | undefined {
  if (report?.status === "preparing" && !report.storedTitle) {
    return "Preparing custom report";
  }

  return report?.title ?? cachedTitle;
}

export function isCustomAnalyticsReportTitleEditable(
  report: CustomAnalyticsReportSummary | null | undefined,
): boolean {
  return !!report && (report.status !== "preparing" || !!report.storedTitle);
}

export function isCustomAnalyticsReportColumnEditingEnabled({
  report,
  isSaving,
}: {
  report: CustomAnalyticsReportSummary | null | undefined;
  isSaving: boolean;
}): boolean {
  return report?.canReorderColumns === true && !isSaving;
}

export function shouldShowCustomAnalyticsReportLoadError({
  report,
  reportIsError,
}: {
  report?: { id: string } | null;
  reportIsError: boolean;
}) {
  return reportIsError && !report;
}

export function shouldShowCustomAnalyticsReportTableError({
  columnsMetadata,
  reportStatus,
  rowsIsError,
  rowsLoaded,
  shouldLoadRows,
}: {
  columnsMetadata: readonly CustomAnalyticsReportColumn[] | null | undefined;
  reportStatus?: CustomAnalyticsReportStatus | null;
  rowsIsError: boolean;
  rowsLoaded: boolean;
  shouldLoadRows: boolean;
}) {
  if (reportStatus === "ready" && columnsMetadata === null) {
    return true;
  }

  return shouldLoadRows && rowsIsError && !rowsLoaded;
}

export function getGeneratedBy(report: CustomAnalyticsReportSummary): string {
  const displayName = report.createdByName ?? report.createdByEmail;
  if (!displayName) {
    return "Unknown";
  }

  if (report.createdByName && report.createdByEmail) {
    return `${report.createdByName} (${report.createdByEmail})`;
  }

  return displayName;
}

export function formatCustomAnalyticsReportTimeZone(timeZone: string): string {
  return getFriendlyTimezoneOption(timeZone).text;
}

export function getCustomReportPageSuggestedQuestions({
  columns,
}: {
  columns: readonly CustomAnalyticsReportColumn[];
}): string[] {
  const questions: string[] = [];
  const explainColumn = getSuggestedExplainColumn(columns);
  if (explainColumn) {
    const noun = isMetricLikeColumn(explainColumn) ? "metric" : "column";
    questions.push(
      `Explain the ${quoteColumnLabel(getColumnQuestionLabel(explainColumn))} ${noun}`,
    );
  }

  questions.push(CUSTOM_REPORT_PAGE_ADD_USEFUL_COLUMNS_QUESTION);
  questions.push(CUSTOM_REPORT_PAGE_EXPLAIN_REPORT_QUESTION);

  return questions.slice(0, CUSTOM_REPORT_PAGE_SUGGESTED_QUESTION_LIMIT);
}

export function buildCustomReportPageContext({
  columns,
  customReportId,
  dataSources,
  report,
  sort,
  timezone,
}: BuildCustomReportPageContextParams): PageContext {
  return {
    pageDescription: report
      ? `User is viewing custom analytics report: ${report.requestedQuestion}`
      : `User is viewing custom analytics report ${customReportId}`,
    preferredAgent: "analytics",
    preferredSkill: "analytics",
    requiredSkill: "analytics",
    suggestedQuestions: getCustomReportPageSuggestedQuestions({ columns }),
    pageData: {
      currentTab: "custom_report",
      customReportId: report?.id ?? customReportId,
      sort: sort
        ? {
            columnKey: sort.key,
            direction: sort.direction,
            columnLabel:
              columns.find((column) => column.key === sort.key)?.label ?? null,
          }
        : null,
      status: report?.status ?? null,
      requestedQuestion: report?.requestedQuestion ?? null,
      reportSpec: report?.reportSpec ?? null,
      explanation: report?.explanation ?? null,
      reportTimeZone: timezone,
      dataSources: dataSources ?? [],
      columns: columns.map((column) => ({
        key: column.key,
        sourceName: column.sourceName,
        label: column.label,
        kind: column.kind,
        help: column.help
          ? {
              description: column.help.description ?? null,
              calculation: column.help.calculation ?? null,
              caveat: column.help.caveat ?? null,
              filters: column.help.filters ?? null,
              source: column.help.source,
            }
          : null,
      })),
    },
  };
}

function getSuggestedExplainColumn(
  columns: readonly CustomAnalyticsReportColumn[],
): CustomAnalyticsReportColumn | null {
  const orderedColumns = getColumnsInDisplayOrder(columns);
  return orderedColumns.find(isMetricLikeColumn) ?? orderedColumns[0] ?? null;
}

function getColumnsInDisplayOrder(
  columns: readonly CustomAnalyticsReportColumn[],
): CustomAnalyticsReportColumn[] {
  return [...columns].sort((left, right) => {
    if (left.order !== right.order) {
      return left.order - right.order;
    }

    return left.label.localeCompare(right.label);
  });
}

function getColumnQuestionLabel(column: CustomAnalyticsReportColumn): string {
  const label = column.label.trim() || column.sourceName.trim();
  return label || column.key;
}

function isMetricLikeColumn(column: CustomAnalyticsReportColumn) {
  return CUSTOM_REPORT_METRIC_COLUMN_KINDS.has(column.kind);
}

function quoteColumnLabel(label: string): string {
  return label.includes('"') ? `'${label}'` : `"${label}"`;
}

export function buildCustomReportRowsPageDataSource({
  columns,
  filtersCount,
  hasMoreRows,
  hiddenColumnCount,
  rows,
  sort,
  status,
}: BuildCustomReportRowsPageDataSourceParams): CustomAnalyticsReportPageDataSource {
  const sortedColumn = sort
    ? columns.find((column) => column.key === sort.key)
    : null;
  const sampleRows =
    status === "ready" ? getCustomReportSampleRows(rows, columns) : [];
  const sampleRowsCapped = sampleRows.length < rows.length;

  return {
    id: "customReportRows",
    label: "Visible custom report rows",
    status,
    summary: getCustomReportRowsPageDataSourceSummary({
      hasMoreRows,
      loadedRowCount: rows.length,
      sampleRowsCapped,
      status,
    }),
    loadedRowCount: rows.length,
    hasMoreRows,
    visibleColumns: columns.map((column) => ({
      key: column.key,
      label: column.label,
      kind: column.kind,
    })),
    hiddenColumnCount,
    filtersCount,
    sort: sort
      ? { columnLabel: sortedColumn?.label ?? null, direction: sort.direction }
      : null,
    sampleRowLimit: CUSTOM_REPORT_PAGE_CONTEXT_SAMPLE_ROW_LIMIT,
    sampleRowsCapped,
    sampleRows,
  };
}

function getCustomReportRowsPageDataSourceSummary({
  hasMoreRows,
  loadedRowCount,
  sampleRowsCapped,
  status,
}: {
  hasMoreRows: boolean;
  loadedRowCount: number;
  sampleRowsCapped: boolean;
  status: CustomAnalyticsReportPageDataSource["status"];
}): string {
  switch (status) {
    case "loading":
      return "The report rows for the current page are still loading.";
    case "ready": {
      const cappingNote = sampleRowsCapped ? " Sample rows are capped." : "";
      return hasMoreRows
        ? `${loadedRowCount} rows are currently loaded for the current report view; more rows can load as the user scrolls.${cappingNote}`
        : `${loadedRowCount} rows are currently loaded for the current report view.${cappingNote}`;
    }
    case "error":
      return "The report rows for the current page failed to load.";
    case "unavailable":
      return "The report rows are not available for the current report view.";
  }
}

export function getCustomReportRowsPageDataSourceStatus({
  reportIsError,
  reportIsPending,
  reportStatus,
  rowsIsError,
  rowsIsPending,
  rowsLoaded,
  shouldLoadRows,
}: {
  reportIsError: boolean;
  reportIsPending: boolean;
  reportStatus?: string;
  rowsIsError: boolean;
  rowsIsPending: boolean;
  rowsLoaded: boolean;
  shouldLoadRows: boolean;
}): CustomAnalyticsReportPageDataSourceStatus {
  if (
    reportIsError ||
    (shouldLoadRows && rowsIsError) ||
    reportStatus === "failed"
  ) {
    return "error";
  }

  if (
    reportIsPending ||
    reportStatus === "preparing" ||
    (shouldLoadRows && rowsIsPending && !rowsLoaded)
  ) {
    return "loading";
  }

  if (!shouldLoadRows) {
    return "unavailable";
  }

  return "ready";
}

function getCustomReportSampleRows(
  rows: readonly Record<string, unknown>[],
  columns: readonly CustomAnalyticsReportColumn[],
): Record<string, unknown>[] {
  const sampleRows: Record<string, unknown>[] = [];
  let remainingCharacterBudget =
    CUSTOM_REPORT_PAGE_CONTEXT_SAMPLE_TOTAL_CHAR_LIMIT;

  for (const row of rows.slice(
    0,
    CUSTOM_REPORT_PAGE_CONTEXT_SAMPLE_ROW_LIMIT,
  )) {
    const sampleRow = Object.fromEntries(
      columns.map((column) => [
        column.label,
        normalizeCustomReportPageContextValue(row[column.key]),
      ]),
    );
    const rowCharacterCount = JSON.stringify(sampleRow).length;
    if (sampleRows.length > 0 && rowCharacterCount > remainingCharacterBudget) {
      break;
    }

    sampleRows.push(sampleRow);
    remainingCharacterBudget -= rowCharacterCount;
  }

  return sampleRows;
}

function normalizeCustomReportPageContextValue(value: unknown): unknown {
  if (
    value == null ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value ?? null;
  }

  if (typeof value === "string") {
    return truncateCustomReportPageContextString(value);
  }

  if (typeof value === "bigint") {
    return value.toString();
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Array.isArray(value)) {
    return value
      .slice(0, CUSTOM_REPORT_PAGE_CONTEXT_ARRAY_ITEM_LIMIT)
      .map(normalizeCustomReportPageContextValue);
  }

  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .slice(0, CUSTOM_REPORT_PAGE_CONTEXT_OBJECT_KEY_LIMIT)
        .map(([key, nestedValue]) => [
          key,
          normalizeCustomReportPageContextValue(nestedValue),
        ]),
    );
  }

  return String(value);
}

function truncateCustomReportPageContextString(value: string): string {
  if (value.length <= CUSTOM_REPORT_PAGE_CONTEXT_STRING_LENGTH_LIMIT) {
    return value;
  }

  return `${value.slice(0, CUSTOM_REPORT_PAGE_CONTEXT_STRING_LENGTH_LIMIT)}...`;
}

function getColumnHeaderMinLength(
  column: Pick<CustomAnalyticsReportColumn, "kind" | "label">,
): number {
  return (
    column.label.length * CUSTOM_REPORT_HEADER_AVERAGE_CHARACTER_WIDTH_PX +
    CUSTOM_REPORT_HEADER_HORIZONTAL_PADDING_PX +
    CUSTOM_REPORT_HEADER_HELP_ICON_WIDTH_PX +
    (column.kind !== "json" ? CUSTOM_REPORT_HEADER_SORT_ICON_WIDTH_PX : 0)
  );
}

function getColumnValueMinLength(
  column: Pick<CustomAnalyticsReportColumn, "kind">,
): number {
  switch (column.kind) {
    case "currency":
    case "integer":
    case "number":
    case "percent":
      return 140;
    case "boolean":
    case "date":
      return 150;
    case "datetime":
      return 190;
    case "json":
    case "text":
      return 180;
  }
}
